/**
 * Created by Rodrigo Quesada on 27/12/16.
 */
import {FormErrors, FormValidator, SpecificValidationMessages} from './validation/form-validator.class';
import {BehaviorSubject, Observable} from 'rxjs';
import {NgForm} from '@angular/forms';
import {DefaultFormValidator} from './validation/default-form-validator.class';
import {publishReplayLastThenConnect} from '../../common/rx/observables.util';
import {Injectable} from '@angular/core';
import {PageStateService} from '../../pages/shared/services/page-state.service';
import {Response} from '@angular/http';
import {BackendError} from '../../common/error/backend-error.class';

// region Other Classes
export enum FormSubmissionStatusCode {
    SUBMITTED,
    NOTSUBMITTED,
    ERROR
}

export enum FormSubmissionStatusReason {
    HAS_ERRORS,
    NOT_DIRTY,
    BAD_REQUEST
}

export class FormSubmissionStatus {
    constructor(readonly code: FormSubmissionStatusCode, readonly reason?: FormSubmissionStatusReason) {
    }
}

export class FormSubmissionResult<T> {
    constructor(readonly status: FormSubmissionStatus, readonly data?: T, readonly error?: BackendError) {
    }
}

type SubmissionResult<D> = FormSubmissionResult<D>;
const SubmissionResult = FormSubmissionResult;
const SubmissionStatus = FormSubmissionStatus;
const Code = FormSubmissionStatusCode;
const Reason = FormSubmissionStatusReason;

// endregion

export class FormManager<D> {

    private readonly _formValidator: FormValidator;
    private _forceErrorDisplay$ = new BehaviorSubject(false);

    readonly hasErrors$: Observable<boolean>;
    readonly displayedErrors$: Observable<FormErrors>;

    constructor(fmgm: FormManager.InjectedMembers,
                private readonly _form$: Observable<NgForm>,
                private readonly _submitData$: Observable<() => Observable<D>>,
                specificValidationMessages$: Observable<SpecificValidationMessages> = Observable.of({}),
                alterUnsavedDataState = true) {
        this._formValidator = new DefaultFormValidator(_form$, specificValidationMessages$);
        // TODO publishReplayLastThenConnect should not be necessary, ask about it on corresponding channel
        // the problem is that publishReplay(1).refCount() on a cold observable that becomes warm after subscription
        // causes stale data when reconnecting (ConnectableObservable.disconnect() => *.connect())
        // it causes replay of value before disconnect immediately followed by newest value caused by new subscription
        this.hasErrors$ = publishReplayLastThenConnect(this._formValidator.hasErrors$);
        this.displayedErrors$ = this._forceErrorDisplay$.switchMap(force => force ? this._formValidator.errors$ : this._formValidator.dirtyErrors$);

        if (alterUnsavedDataState) this._form$.switchMap(it => it.form.statusChanges)
            .switchMap(() => this._form$)
            .map(it => it.form.dirty)
            .filter(it => it)
            .subscribe(dirty => fmgm.pageStateService.addUnsavedDataState());
    }

    reset() {
        this._form$.first().subscribe(ngForm => ngForm.form.markAsPristine());
        this._forceErrorDisplay$.next(false);
    }

    validate() {
        this._forceErrorDisplay$.next(true);
    }

    cleanErrors() {
        this._forceErrorDisplay$.next(false);
    }

    save(ignoreErrors = false, forceSave = false): Observable<SubmissionResult<D>> {
        return this._form$.first().flatMap(form => {
            this._forceErrorDisplay$.next(true);
            if (form.dirty || forceSave) {
                let submitData$ = this._submitData$.flatMap(fn => fn())
                    .map(data => new SubmissionResult(new SubmissionStatus(Code.SUBMITTED), data))
                    .catch((res: Response|ResponseError) => {
                        let error: BackendError|undefined;

                        if (isError(res)) throw res;
                        else try { error = res.json(); } catch (e) {}

                        return Observable.of(new SubmissionResult(new SubmissionStatus(Code.ERROR, FormSubmissionStatusReason.BAD_REQUEST), undefined, error));
                    });

                return ignoreErrors ? submitData$ : this.hasErrors$.first().flatMap(hasErrors => {

                    return !hasErrors ? submitData$
                        : Observable.of(new SubmissionResult(new SubmissionStatus(Code.NOTSUBMITTED, Reason.HAS_ERRORS)));
                });
            }
            else return Observable.of(new SubmissionResult(new SubmissionStatus(Code.NOTSUBMITTED, Reason.NOT_DIRTY)));
        }).first().publishReplay(1).refCount();

        //region Utils
        type ResponseError = { error?: string, status?: number };

        function isError(obj: any): obj is ResponseError {
            const objAsError = obj as ResponseError;
            return objAsError.error !== undefined || objAsError.status !== undefined;
        }

        //endregion
    }
}

// region Injection
export module FormManager {
    @Injectable()
    export class InjectedMembers {
        constructor(readonly pageStateService: PageStateService) {}
    }
}
// endregion



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/form-manager.class.ts